import axios from "axios";
import dayjs from "dayjs";
import { logout, setConfig } from "appredux/configSlice";
import store from "store";
import {
  clearAuthTokens,
  getExpiresAt,
  getRefreshToken,
  getToken,
  setExpiresAt,
  setRefreshExpiresAt,
  setRefreshToken,
  setToken,
} from "./cookies";
import { generateBaseUri, getOrCreateDeviceToken } from "./helper";
import { resetChat } from "appredux/chatSlice";
import * as Sentry from '@sentry/react'

// Timeout default request normal. Dulu 100s — terlalu lama, request yang
// menggantung baru di-abort setelah 100s sehingga jadi bloker (cursor macet,
// spam timeout di Sentry). 30s cukup untuk request normal; endpoint yang memang
// berat (upload/download media) memakai override lebih panjang per-request.
export const DEFAULT_TIMEOUT_MS = 30000;
// Override untuk operasi berat (file besar): upload media & download blob.
export const LONG_TIMEOUT_MS = 120000;

const customAxios = axios.create({
  baseURL: generateBaseUri(),
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${getToken()}`,
    "X-App": "wa",
  },
  timeout: DEFAULT_TIMEOUT_MS,
});

const isAuthorization = (config = {}) => {
  return !!config.headers?.Authorization;
};

export const requestHandler = (request) => {
  if (isAuthorization(request)) {
    const token = getToken();
    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
    }
  } else {
    const token = getOrCreateDeviceToken();
    request.headers["X-Device-Token"] = token;
    request.headers["X-Device-Agent"] = navigator.userAgent;
  }
  return request;
};

// ============== JWT REFRESH TOKEN MECHANISM ==============
// Refresh dijalankan saat request kena 401, atau proaktif saat token hampir expired dan
// tab sedang fokus (startFocusRefresh). Tab background tidak pernah refresh sendiri.

const REFRESH_URL = "/authentication/refresh";
// Refresh berjalan di dalam Web Lock lintas tab → jangan sampai menggantung lama
const REFRESH_TIMEOUT_MS = 30000;
// Nama Web Lock untuk refresh lintas tab, supaya beberapa tab yang kena 401 bersamaan
// cukup memicu satu request /refresh (kalau BE me-rotate refresh token, mencegah logout).
const REFRESH_LOCK_NAME = "kledo-chat-auth-refresh";
const FRESH_TOKEN_BUFFER_MS = 60 * 1000;
const FOCUS_REFRESH_INTERVAL_MS = 30 * 1000;

// Single-flight refresh per tab: semua 401 yang bersamaan + ensureFreshToken menunggu
// promise yang sama, supaya tidak ada dua request /refresh berbarengan
let refreshPromise = null;

// Response BE bisa bertingkat (mis. { data: { data: {...} } } seperti response login)
const extractTokenData = (body) =>
  [body?.data?.data, body?.data, body].find((item) => item?.access_token);

// Struktur key response (tanpa nilai) untuk diagnosa, supaya token tidak ikut terkirim
const describeShape = (value, depth = 0) => {
  if (!value || typeof value !== "object" || depth > 2) return typeof value;
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, describeShape(item, depth + 1)])
  );
};

const saveRefreshedTokens = (body) => {
  const tokenData = extractTokenData(body);
  if (!tokenData) {
    Sentry.captureMessage("Invalid refresh token response", {
      extra: { shape: describeShape(body) },
    });
    throw new Error("Invalid refresh response");
  }
  setToken(tokenData.access_token);
  // BE tidak selalu me-rotate refresh token → pertahankan yang lama jika tidak dikirim
  if (tokenData.refresh_token) {
    setRefreshToken(tokenData.refresh_token);
  }
  if (tokenData.refresh_expires_at) {
    setRefreshExpiresAt(tokenData.refresh_expires_at);
  }
  // expires_at milik access token lama tidak lagi berlaku
  setExpiresAt(tokenData.expires_at ?? null);

  window.appReduxStore?.dispatch(
    setConfig({
      apiToken: tokenData.access_token,
      expiredToken: tokenData.expires_at ?? null,
    })
  );
};

const requestRefresh = async (refreshToken) => {
  const { data } = await customAxios.post(
    REFRESH_URL,
    { refresh_token: refreshToken },
    {
      baseURL: generateBaseUri({ ignoreInitEndpoint: true }),
      headers: { Authorization: false },
      timeout: REFRESH_TIMEOUT_MS,
      _isRefreshRequest: true,
    }
  );
  saveRefreshedTokens(data);
};

/**
 * Jalankan refresh token dengan single-flight (per tab) + Web Lock (lintas tab).
 */
const runRefresh = () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const usedRefreshToken = getRefreshToken();
  const usedAccessToken = getToken();
  if (!usedRefreshToken) {
    return Promise.reject(new Error("No refresh token"));
  }

  const doRefresh = async () => {
    const latestRefreshToken = getRefreshToken();
    // Tab lain sudah logout selama menunggu lock
    if (!latestRefreshToken) {
      throw new Error("No refresh token");
    }
    // Tab lain sudah refresh selama menunggu lock → pakai token hasilnya. Access token ikut
    // dicek karena BE tidak selalu me-rotate refresh token.
    if (
      latestRefreshToken !== usedRefreshToken ||
      getToken() !== usedAccessToken
    ) {
      return;
    }
    await requestRefresh(latestRefreshToken);
  };

  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  const request = locks?.request
    ? locks.request(REFRESH_LOCK_NAME, doRefresh)
    : doRefresh();

  refreshPromise = request.finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

/**
 * Waktu expired access token (milliseconds).
 * Utamakan claim `exp` JWT (epoch UTC) — `expires_at` dari BE tanpa zona waktu dan diparse
 * sebagai waktu lokal browser, jadi meleset untuk user di luar WIB.
 * @returns {number|null}
 */
const getTokenExpiryMs = () => {
  const payload = getToken()?.split(".")[1];
  if (payload) {
    try {
      const { exp } = JSON.parse(
        atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
      );
      if (exp) {
        return exp * 1000;
      }
    } catch {
      // Bukan JWT → fallback ke expires_at
    }
  }
  const expiresAt = getExpiresAt();
  const parsed = expiresAt ? dayjs(expiresAt) : null;
  return parsed?.isValid() ? parsed.valueOf() : null;
};

const isTokenNearExpiry = () => {
  const expiryMs = getTokenExpiryMs();
  return !!expiryMs && expiryMs - Date.now() <= FRESH_TOKEN_BUFFER_MS;
};

/**
 * Pastikan access token siap pakai untuk request yang tidak lewat customAxios
 * (mis. auth channel Pusher). Refresh hanya jika token sudah / hampir expired.
 * @returns {Promise<string|null>} token terbaru, atau null jika sesi sudah tidak valid
 */
export const ensureFreshToken = async () => {
  const currentToken = getToken();
  if (!currentToken) {
    return null;
  }

  // Token masih jauh dari expired, atau sesi tanpa refresh_token
  if (!getRefreshToken() || !isTokenNearExpiry()) {
    return currentToken;
  }

  try {
    await runRefresh();
  } catch (error) {
    console.warn("Ensure fresh token failed:", error);
  }

  return getToken() || null;
};

// Dimatikan jika jam perangkat meleset (token baru tetap terlihat expired) → cukup jalur 401
let isFocusRefreshDisabled = false;

const refreshIfNearExpiry = async () => {
  if (
    isFocusRefreshDisabled ||
    document.visibilityState !== "visible" ||
    !document.hasFocus() ||
    !getRefreshToken() ||
    !isTokenNearExpiry()
  ) {
    return;
  }

  try {
    await runRefresh();
    if (isTokenNearExpiry()) {
      isFocusRefreshDisabled = true;
    }
  } catch (error) {
    // Gagal di sini tidak fatal — request berikutnya tetap ditangani jalur 401
    console.warn("Focus refresh failed:", error);
  }
};

/**
 * Refresh proaktif saat tab kembali fokus/terlihat, dan cek berkala selama fokus,
 * supaya klik pertama user tidak perlu menunggu 401 → refresh → retry.
 * Dipanggil sekali saat aplikasi dimulai (main.jsx).
 */
export const startFocusRefresh = () => {
  window.addEventListener("focus", refreshIfNearExpiry);
  document.addEventListener("visibilitychange", refreshIfNearExpiry);
  setInterval(refreshIfNearExpiry, FOCUS_REFRESH_INTERVAL_MS);
};

/** Hapus sesi dan reset aplikasi; MainLayout akan mengarahkan ke /user/login */
const clearAuthAndLogout = () => {
  clearAuthTokens();
  if (window.location.pathname === "/user/login") {
    return;
  }
  // Dihapus dulu previous path dan selected endpoinnya
  store.remove("app.endpoint");
  store.remove("app.bootstrapEndpoint");
  store.remove("app.previousPathname");
  customAxios.defaults.baseURL = generateBaseUri();
  // Reset reduxnya
  window.appReduxStore?.dispatch(logout());
  window.appReduxStore?.dispatch(resetChat());
};

/**
 * Response interceptor autentikasi (dipasang sekali, sebelum interceptor notifikasi).
 * Menangani 401 dengan:
 * - Stale token: token sudah diperbarui request/tab lain → retry tanpa refresh
 * - runRefresh: single-flight per tab + Web Lock lintas tab, lalu retry
 * - _retry flag mencegah infinite loop
 * Opt-out per request: `_skipAuthRefresh: true` (mis. logout).
 */
export const authErrorHandler = (error) => {
  const originalRequest = error?.config;
  const status = error?.response?.status;

  // Request publik (login, OTP, register, dll): 401 = kredensial salah, bukan sesi expired
  if (
    status !== 401 ||
    !originalRequest ||
    originalRequest._skipAuthRefresh ||
    !isAuthorization(originalRequest)
  ) {
    if (status === 401 && originalRequest?._isRefreshRequest) {
      clearAuthAndLogout();
    }
    return Promise.reject(error);
  }

  // Retry masih 401 → sesi memang tidak valid
  if (originalRequest._retry || !getRefreshToken()) {
    clearAuthAndLogout();
    return Promise.reject(error);
  }

  // Token sudah diperbarui request / tab lain setelah request ini dikirim → cukup retry dengan
  // token terbaru. Tidak set _retry: kalau masih 401, tetap boleh lewat jalur refresh.
  const latestToken = getToken();
  if (
    !originalRequest._staleTokenRetry &&
    latestToken &&
    originalRequest.headers.Authorization !== `Bearer ${latestToken}`
  ) {
    originalRequest._staleTokenRetry = true;
    return customAxios(originalRequest);
  }

  originalRequest._retry = true;

  return runRefresh()
    .then(() => customAxios(originalRequest))
    .catch((refreshError) => {
      // Retry yang gagal (mis. 422) diteruskan apa adanya
      if (refreshError?.config && !refreshError.config._isRefreshRequest) {
        return Promise.reject(refreshError);
      }
      // 401 dari /refresh sudah di-logout di atas. Refresh token hilang (tab lain logout
      // selama menunggu lock) atau response refresh tidak valid juga berarti sesi habis.
      // 429 / network error / 5xx: sesi belum tentu habis → jangan logout.
      const refreshStatus = refreshError?.response?.status;
      if (!refreshError?.config || refreshStatus === 401 || !getRefreshToken()) {
        clearAuthAndLogout();
      }
      // Kembalikan error refresh supaya pesan (mis. rate limit) tetap tampil
      return Promise.reject(refreshError?.config ? refreshError : error);
    });
};

export const errorHandler = (error, pathname, notification) => {
  if (axios.isCancel(error)) {
    return Promise.reject(error);
  }
  // Error yang sama bisa melewati interceptor ini dua kali (request asli + retry
  // setelah refresh) → cukup tangani sekali
  if (error?._isNotified) {
    return Promise.reject(error);
  }
  if (error && typeof error === "object") {
    error._isNotified = true;
  }

  const status = error.response && error.response.status;
  let errorMessage = error?.response?.data?.message || "Network error";

  switch (status) {
    case 401:
      // Sesi sudah dibersihkan oleh authErrorHandler
      if (
        pathname !== "/user/login" &&
        (isAuthorization(error.config) || error.config?._isRefreshRequest)
      ) {
        errorMessage = "Login expired";
      }
      break;

    case 400: // Bad Request
      break;
    // case 404: // Not Found
    //   break;
    case status >= 500: // Server error
      break;

    case 422:
      Sentry.captureException(errorMessage);
      break;
    default:
      break;
  }
  if (
    errorMessage &&
    errorMessage !== "Network error" &&
    !error?.config?.skipErrorNotification
  ) {
    notification.warning({
      // message: title,
      description: errorMessage,
      key: errorMessage,
    });
  }

  return Promise.reject(error);
};

export const successHandler = (response) => {
  return response;
};

export const setBaseUrl = (url) => {
  customAxios.defaults.baseURL = url;
};

// Create custom axios GET with cancellation
export const makeGetRequestCreator = () => {
  let call;
  return (url, params) => {
    if (call) {
      call.cancel("Only one request allowed at a time.");
    }
    call = axios.CancelToken.source();
    return customAxios.get(url, {
      cancelToken: call.token,
      params,
    });
  };
};

export const getOnce = makeGetRequestCreator();

customAxios.interceptors.request.use(
  (request) => requestHandler(request),
  (error) => {
    // Tangani kesalahan yang terjadi selama proses permintaan
    console.log("Request Interceptor - Error:", error);
    return Promise.reject(error);
  }
);
// Interceptor auth dipasang sekali di sini (bukan di Layout yang dipasang ulang tiap
// pindah halaman) dan terdaftar lebih dulu, jadi jalan sebelum interceptor notifikasi
customAxios.interceptors.response.use(undefined, authErrorHandler);
// customAxios.interceptors.response.use(
//   (response) => successHandler(response),
//   (error) => errorHandler(error)
// );

export default customAxios;
